import * as THREE from 'three';
import {
  createFpsHandGroup,
  playerArmMaterials,
  type ArmGripStyle,
} from '../player/ProcArms';
import {
  applyStance,
  bakedPunchClip,
  punchPlayDuration,
  samplePunchClip,
  type FistXform,
  type PunchClip,
  type PunchClipId,
} from '../player/PunchClip';

export type EnemyVariantId =
  | 'guard'
  | 'scientist'
  | 'jumpsuit'
  | 'camo'
  | 'suit'
  | 'suitNavy'
  | 'agent';
export type HeadgearId = 'none' | 'hat' | 'helmet';
export type HairStyle = 'cap' | 'recede' | 'puff' | 'bald' | 'part';
export type TieStyle = 'none' | 'navy' | 'red' | 'gold';

export interface CharLook {
  skin: string;
  hair: string;
  hairStyle: HairStyle;
  glasses: boolean;
  tie: TieStyle;
  mustache: boolean;
  thickBrows: boolean;
  highHairline: boolean;
  partFace: boolean;
}

export type ProcEnemyKind = 'pistol' | 'shotgun' | 'melee' | 'mp5' | 'ak47' | 'sawedoff' | 'scientist' | 'civilian';

export interface ProcAnimEnemy {
  model: THREE.Object3D;
  kind: ProcEnemyKind;
  currentClip: string;
  layerMode: 'full' | 'aimloco';
  upperClip: string;
  lowerClip: string;
  animT: number;
  attackPhase: 'none' | 'aim' | 'windup' | 'swing' | 'shoot';
  attackLockT: number;
  hurtT?: number;
  /** 0 stand … 1 fully sitting (toilet sitter). */
  sitBlend?: number;
  /** 0 stand … 1 kneeling fire. Smoothed in applyProcEnemyPose. */
  kneelBlend?: number;
  /** If set, kneelBlend damps toward this; otherwise inferred from the kneel clip. */
  kneelTarget?: number;
  punchId?: PunchClipId;
  punchComboStep?: number;
  /** When set, fade the idle head-scan toward a locked aim pose. */
  lockHead?: boolean;
  /** 1 = full look-around, 0 = head locked. Driven by applyProcEnemyPose. */
  lookScan?: number;
}

export type FaceMood = 'calm' | 'angry' | 'scared';
export type MouthFrame = 0 | 1 | 2 | 3;

export interface EnemyFace {
  mesh: THREE.Mesh;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  tex: THREE.CanvasTexture;
  skin: string;
  hair: string;
  mood: FaceMood;
  mouth: MouthFrame;
  blink: boolean;
  blinkWait: number;
  blinkShut: number;
  mustache: boolean;
  thickBrows: boolean;
  highHairline: boolean;
  partFace: boolean;
}

interface VariantMats {
  face: THREE.MeshStandardMaterial;
  clothes: THREE.MeshStandardMaterial;
  sleeve: THREE.MeshStandardMaterial;
  pants: THREE.MeshStandardMaterial;
  skin: THREE.MeshStandardMaterial;
  hair: THREE.MeshStandardMaterial;
  boots: THREE.MeshStandardMaterial;
}

export interface ProcEnemyKit {
  variants: Record<EnemyVariantId, VariantMats>;
}

const PROC_CLIPS = [
  'idle',
  'walk',
  'run',
  'aim',
  'shoot',
  'kneel',
  'sidestep',
  'hop',
  'lookaround',
  'point',
  'guard',
  'melee',
  'hit',
  'hurt_foot',
  'hurt_arm',
  'death',
] as const;

export function procClipSet(): Set<string> {
  return new Set(PROC_CLIPS);
}

export function pickEnemyVariant(kind: ProcEnemyKind, n: number): EnemyVariantId {
  if (kind === 'scientist') return 'scientist';
  if (kind === 'civilian') return ((n >> 1) ^ (n >> 2)) & 1 ? 'suitNavy' : 'suit';
  if (kind === 'melee') return 'guard';
  if (kind === 'ak47') return 'agent';
  const cycle: EnemyVariantId[] = ['guard', 'camo', 'jumpsuit'];
  return cycle[n % 3]!;
}

export function pickGuardHeadgear(kind: ProcEnemyKind, n: number): HeadgearId {
  if (kind === 'scientist' || kind === 'civilian') return 'none';
  if (kind === 'melee') return 'hat';
  const gear: HeadgearId[] = ['hat', 'helmet', 'none'];
  return gear[n % 3]!;
}

const LOOKS: Array<{ skin: string; hair: string }> = [
  { skin: '#c4a07a', hair: '#1a1410' },
  { skin: '#d4b496', hair: '#3a3028' },
  { skin: '#b88860', hair: '#201810' },
  { skin: '#a87850', hair: '#2a2018' },
  { skin: '#c8a888', hair: '#141210' },
  { skin: '#8a5a38', hair: '#0e0c0a' },
  { skin: '#d8c0a0', hair: '#6a4a28' },
  { skin: '#c4a07a', hair: '#5a5450' },
];

const HAIR_STYLES: HairStyle[] = ['cap', 'recede', 'puff', 'bald', 'part'];
const TIES: TieStyle[] = ['navy', 'red', 'gold', 'none'];

export const DEFAULT_LOOK: CharLook = {
  skin: '#c4a07a',
  hair: '#0e0c0a',
  hairStyle: 'part',
  glasses: false,
  tie: 'red',
  mustache: false,
  thickBrows: false,
  highHairline: false,
  partFace: true,
};

export function pickCharLook(kind: ProcEnemyKind, n: number): CharLook {
  const pal = LOOKS[n % LOOKS.length]!;
  const hairStyle = HAIR_STYLES[(n * 2 + 1) % HAIR_STYLES.length]!;
  const glasses = kind === 'scientist' ? n % 3 !== 1 : kind === 'civilian' ? ((n >> 2) & 1) === 1 : false;
  const tie =
    kind === 'ak47' ? 'red' : kind === 'scientist' || kind === 'civilian' ? TIES[n % 4]! : 'none';
  return {
    skin: pal.skin,
    hair: pal.hair,
    hairStyle,
    glasses,
    tie,
    mustache: n % 5 === 2,
    thickBrows: n % 3 === 0,
    highHairline: n % 4 === 3,
    partFace: hairStyle === 'part',
  };
}

export function enemyGripStyle(kind: ProcEnemyKind): ArmGripStyle {
  if (kind === 'melee' || kind === 'scientist' || kind === 'civilian') return 'fists';
  if (kind === 'pistol') return 'm9';
  if (kind === 'shotgun') return 'm870';
  if (kind === 'sawedoff') return 'sawedoff';
  return 'longGun';
}

function paintNearest(size: number, draw: (ctx: CanvasRenderingContext2D, s: number) => void): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  draw(ctx, size);
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

function mat(map: THREE.Texture | null, color: number, rough = 0.85): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    map: map ?? undefined,
    color,
    roughness: rough,
    metalness: 0.04,
    flatShading: true,
    side: THREE.DoubleSide,
  });
}

function paintAgentFace(skin: string, hair: string): THREE.CanvasTexture {
  return paintNearest(64, (ctx, s) => {
    ctx.fillStyle = skin;
    ctx.fillRect(0, 0, s, s);
    ctx.fillStyle = hair;
    ctx.fillRect(0, 0, s, 16);
    ctx.fillRect(0, 12, 16, 18);
    ctx.fillRect(s - 9, 12, 9, 22);
    ctx.fillRect(24, 12, 40, 9);
    ctx.fillStyle = skin;
    ctx.fillRect(17, 0, 5, 18);
    ctx.fillStyle = '#fff6e8';
    ctx.fillRect(8, 26, 18, 14);
    ctx.fillRect(38, 26, 18, 14);
    ctx.fillStyle = '#1a1208';
    ctx.fillRect(13, 30, 8, 8);
    ctx.fillRect(43, 30, 8, 8);
    ctx.fillStyle = skin;
    ctx.fillRect(26, 36, 12, 8);
    ctx.fillStyle = '#5a2018';
    ctx.fillRect(20, 50, 24, 5);
    ctx.fillStyle = '#2a100c';
    ctx.fillRect(24, 51, 16, 3);
  });
}

function paintFace(skin: string, hair: string): THREE.CanvasTexture {
  return paintNearest(64, (ctx, s) => {
    ctx.fillStyle = skin;
    ctx.fillRect(0, 0, s, s);
    ctx.fillStyle = hair;
    ctx.fillRect(0, 0, s, 22);
    ctx.fillRect(0, 18, 12, 16);
    ctx.fillRect(s - 12, 18, 12, 16);
    ctx.fillStyle = '#fff6e8';
    ctx.fillRect(8, 26, 18, 14);
    ctx.fillRect(38, 26, 18, 14);
    ctx.fillStyle = '#1a1208';
    ctx.fillRect(13, 30, 8, 8);
    ctx.fillRect(43, 30, 8, 8);
    ctx.fillStyle = skin;
    ctx.fillRect(26, 36, 12, 8);
    ctx.fillStyle = '#5a2018';
    ctx.fillRect(20, 50, 24, 5);
    ctx.fillStyle = '#2a100c';
    ctx.fillRect(24, 51, 16, 3);
  });
}

function drawBaseFace(
  ctx: CanvasRenderingContext2D,
  s: number,
  skin: string,
  hair: string,
  feats: Pick<EnemyFace, 'partFace' | 'highHairline' | 'thickBrows' | 'mustache'>,
): void {
  ctx.fillStyle = skin;
  ctx.fillRect(0, 0, s, s);
  ctx.fillStyle = hair;
  if (feats.partFace) {
    ctx.fillRect(0, 0, s, 16);
    ctx.fillRect(0, 12, 16, 18);
    ctx.fillRect(s - 9, 12, 9, 22);
    ctx.fillRect(24, 12, 40, 9);
    ctx.fillStyle = skin;
    ctx.fillRect(17, 0, 5, 18);
  } else if (feats.highHairline) {
    ctx.fillRect(0, 0, s, 14);
    ctx.fillRect(0, 12, 10, 12);
    ctx.fillRect(s - 10, 12, 10, 12);
  } else {
    ctx.fillRect(0, 0, s, 22);
    ctx.fillRect(0, 18, 12, 16);
    ctx.fillRect(s - 12, 18, 12, 16);
  }
  if (feats.thickBrows) {
    ctx.fillStyle = hair;
    ctx.fillRect(7, 22, 20, 4);
    ctx.fillRect(37, 22, 20, 4);
  }
  ctx.fillStyle = '#fff6e8';
  ctx.fillRect(8, 26, 18, 14);
  ctx.fillRect(38, 26, 18, 14);
  ctx.fillStyle = '#1a1208';
  ctx.fillRect(13, 30, 8, 8);
  ctx.fillRect(43, 30, 8, 8);
  ctx.fillStyle = skin;
  ctx.fillRect(26, 36, 12, 8);
  if (feats.mustache) {
    ctx.fillStyle = hair;
    ctx.fillRect(22, 42, 20, 4);
  }
}

function drawAngryOverlay(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = '#1a1208';
  ctx.save();
  ctx.translate(17, 24);
  ctx.rotate(0.38);
  ctx.fillRect(-11, -2, 22, 4);
  ctx.restore();
  ctx.save();
  ctx.translate(47, 24);
  ctx.rotate(-0.38);
  ctx.fillRect(-11, -2, 22, 4);
  ctx.restore();
  ctx.fillStyle = '#c01818';
  ctx.fillRect(13, 30, 8, 8);
  ctx.fillRect(43, 30, 8, 8);
}

function drawScaredOverlay(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = '#fff6e8';
  ctx.fillRect(6, 24, 22, 18);
  ctx.fillRect(36, 24, 22, 18);
  ctx.fillStyle = '#1a1208';
  ctx.fillRect(12, 30, 10, 10);
  ctx.fillRect(42, 30, 10, 10);
  ctx.fillStyle = '#3a2018';
  ctx.fillRect(8, 22, 18, 3);
  ctx.fillRect(38, 22, 18, 3);
}

function drawMouth(ctx: CanvasRenderingContext2D, skin: string, frame: MouthFrame): void {
  ctx.fillStyle = skin;
  ctx.fillRect(16, 46, 32, 16);
  if (frame === 0) {
    ctx.fillStyle = '#5a2018';
    ctx.fillRect(20, 50, 24, 5);
    ctx.fillStyle = '#2a100c';
    ctx.fillRect(24, 51, 16, 3);
    return;
  }
  if (frame === 1) {
    ctx.fillStyle = '#3a1814';
    ctx.fillRect(22, 49, 20, 7);
    ctx.fillStyle = '#8a3030';
    ctx.fillRect(24, 51, 16, 3);
    return;
  }
  if (frame === 2) {
    ctx.fillStyle = '#2a1010';
    ctx.fillRect(21, 48, 22, 10);
    ctx.fillStyle = '#6a2020';
    ctx.fillRect(24, 50, 16, 5);
    return;
  }
  ctx.fillStyle = '#1a0808';
  ctx.fillRect(18, 47, 28, 12);
  ctx.fillStyle = '#801818';
  ctx.fillRect(22, 50, 20, 6);
}

function drawClosedLids(ctx: CanvasRenderingContext2D, skin: string): void {
  ctx.fillStyle = skin;
  ctx.fillRect(6, 24, 22, 18);
  ctx.fillRect(36, 24, 22, 18);
  ctx.fillStyle = '#4a2a20';
  ctx.fillRect(8, 31, 18, 3);
  ctx.fillRect(38, 31, 18, 3);
}

function paintEnemyFace(face: EnemyFace): void {
  const { ctx, canvas, skin, hair } = face;
  drawBaseFace(ctx, canvas.width, skin, hair, face);
  if (face.mood === 'angry') drawAngryOverlay(ctx);
  else if (face.mood === 'scared') drawScaredOverlay(ctx);
  if (face.blink) drawClosedLids(ctx, skin);
  drawMouth(ctx, skin, face.mouth);
  face.tex.needsUpdate = true;
}

export function cloneEnemyFace(model: THREE.Object3D, look: CharLook = DEFAULT_LOOK): EnemyFace | null {
  const mesh = model.getObjectByName('face') as THREE.Mesh | undefined;
  if (!mesh?.isMesh) return null;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.MeshStandardMaterial({
    map: tex,
    color: 0xffffff,
    roughness: 0.78,
    metalness: 0.04,
    flatShading: true,
    side: THREE.FrontSide,
  });
  mesh.material = mat;
  const face: EnemyFace = {
    mesh,
    canvas,
    ctx,
    tex,
    skin: look.skin,
    hair: look.hair,
    mood: 'calm',
    mouth: 0,
    blink: false,
    blinkWait: 0.6 + Math.random() * 3.2,
    blinkShut: 0,
    mustache: look.mustache,
    thickBrows: look.thickBrows,
    highHairline: look.highHairline,
    partFace: look.partFace,
  };
  paintEnemyFace(face);
  return face;
}

export function setEnemyFaceMood(face: EnemyFace | null, mood: FaceMood): void {
  if (!face || face.mood === mood) return;
  face.mood = mood;
  paintEnemyFace(face);
}

export function setEnemyMouthFrame(face: EnemyFace | null, mouth: MouthFrame): void {
  if (!face || face.mouth === mouth) return;
  face.mouth = mouth;
  paintEnemyFace(face);
}

export function tickEnemyBlink(face: EnemyFace | null, dt: number): void {
  if (!face) return;
  if (face.blink) {
    face.blinkShut -= dt;
    if (face.blinkShut > 0) return;
    face.blink = false;
    face.blinkWait = Math.random() < 0.2 ? 0.1 + Math.random() * 0.12 : 2.1 + Math.random() * 3.8;
    paintEnemyFace(face);
    return;
  }
  face.blinkWait -= dt;
  if (face.blinkWait > 0) return;
  face.blink = true;
  face.blinkShut = 0.07 + Math.random() * 0.06;
  paintEnemyFace(face);
}

/** Front (u 0–0.5) and back (u 0.5–1) panels so ties / badges stay on the chest. */
function paintClothes(
  base: string,
  front: (ctx: CanvasRenderingContext2D, s: number) => void,
  back?: (ctx: CanvasRenderingContext2D, s: number) => void,
): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 64;
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  const panel = (ox: number, draw: (ctx: CanvasRenderingContext2D, s: number) => void) => {
    ctx.save();
    ctx.translate(ox, 0);
    ctx.beginPath();
    ctx.rect(0, 0, 64, 64);
    ctx.clip();
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, 64, 64);
    draw(ctx, 64);
    ctx.restore();
  };
  panel(0, front);
  panel(64, back ?? (() => {}));
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

export function createProcEnemyKit(): ProcEnemyKit {
  const mk = (
    skinHex: number,
    clothes: THREE.CanvasTexture,
    sleeveHex: number,
    pantsCol: number,
    hairHex: number,
    bootsHex: number,
    face: THREE.CanvasTexture,
  ): VariantMats => ({
    face: Object.assign(mat(face, 0xffffff, 0.78), { side: THREE.FrontSide }),
    clothes: mat(clothes, 0xffffff, 0.88),
    sleeve: mat(null, sleeveHex, 0.88),
    pants: mat(null, pantsCol, 0.9),
    skin: mat(null, skinHex, 0.72),
    hair: mat(null, hairHex, 0.62),
    boots: mat(null, bootsHex, 0.7),
  });

  const guardBands = (ctx: CanvasRenderingContext2D, s: number) => {
    ctx.fillStyle = '#243c1e';
    ctx.fillRect(0, 0, s, 8);
    ctx.fillRect(0, s - 6, s, 6);
    ctx.fillStyle = '#1a1814';
    ctx.fillRect(0, 22, s, 4);
  };
  const guardCloth = paintClothes(
    '#35582c',
    (ctx, s) => {
      guardBands(ctx, s);
      ctx.fillStyle = '#c8b45a';
      ctx.fillRect(30, 12, 6, 8);
    },
    guardBands,
  );
  const sciCloth = paintClothes(
    '#e8e6dc',
    (ctx) => {
      ctx.fillStyle = '#1c2a4a';
      ctx.fillRect(28, 8, 8, 40);
      ctx.fillStyle = '#c03028';
      ctx.fillRect(30, 18, 4, 8);
    },
    (ctx, s) => {
      ctx.fillStyle = '#d0cec4';
      ctx.fillRect(0, 0, s, 7);
    },
  );
  const jumpStripes = (ctx: CanvasRenderingContext2D, s: number) => {
    ctx.fillStyle = '#8a3a0c';
    ctx.fillRect(0, 18, s, 6);
    ctx.fillRect(26, 0, 12, s);
  };
  const jumpCloth = paintClothes('#d06018', jumpStripes, jumpStripes);
  const camoCloth = paintClothes(
    '#4a5a32',
    (ctx) => {
      ctx.fillStyle = '#2e3a1c';
      ctx.fillRect(6, 8, 18, 14);
      ctx.fillRect(36, 22, 20, 16);
      ctx.fillRect(10, 40, 22, 12);
      ctx.fillStyle = '#6a7a44';
      ctx.fillRect(24, 4, 16, 10);
      ctx.fillRect(4, 28, 14, 10);
    },
    (ctx) => {
      ctx.fillStyle = '#2e3a1c';
      ctx.fillRect(28, 6, 16, 12);
      ctx.fillRect(8, 26, 18, 14);
      ctx.fillRect(34, 42, 20, 10);
      ctx.fillStyle = '#6a7a44';
      ctx.fillRect(4, 8, 14, 10);
      ctx.fillRect(40, 20, 12, 12);
    },
  );
  const suitCloth = paintClothes(
    '#1a1a1c',
    (ctx) => {
      ctx.fillStyle = '#eceae4';
      ctx.fillRect(18, 0, 28, 36);
      ctx.fillStyle = '#1a1a1c';
      ctx.fillRect(28, 0, 8, 36);
      ctx.fillStyle = '#4a6aa0';
      ctx.fillRect(30, 8, 4, 20);
    },
    (ctx, s) => {
      ctx.fillStyle = '#121214';
      ctx.fillRect(30, 0, 4, s);
    },
  );
  const navyCloth = paintClothes(
    '#1a2a4a',
    (ctx) => {
      ctx.fillStyle = '#eceae4';
      ctx.fillRect(18, 0, 28, 36);
      ctx.fillStyle = '#1a2a4a';
      ctx.fillRect(28, 0, 8, 36);
      ctx.fillStyle = '#8a2018';
      ctx.fillRect(30, 8, 4, 20);
    },
    (ctx, s) => {
      ctx.fillStyle = '#121c30';
      ctx.fillRect(30, 0, 4, s);
    },
  );
  const agentCloth = paintClothes(
    '#121214',
    (ctx) => {
      ctx.fillStyle = '#f2eee6';
      ctx.fillRect(20, 0, 24, 34);
      ctx.fillStyle = '#0a0a0c';
      ctx.fillRect(18, 0, 6, 36);
      ctx.fillRect(40, 0, 6, 36);
      ctx.fillStyle = '#c4181c';
      ctx.fillRect(30, 4, 5, 28);
      ctx.fillStyle = '#8a1014';
      ctx.fillRect(31, 4, 3, 6);
    },
    (ctx, s) => {
      ctx.fillStyle = '#0a0a0c';
      ctx.fillRect(30, 0, 4, s);
    },
  );

  return {
    variants: {
      guard: mk(0xc4a07a, guardCloth, 0x35582c, 0x2a3e22, 0x1a1410, 0x1c1c1c, paintFace('#c4a07a', '#1a1410')),
      scientist: mk(0xd4b496, sciCloth, 0xe8e6dc, 0x2c2c34, 0x3a3028, 0x2a2a2a, paintFace('#d4b496', '#3a3028')),
      jumpsuit: mk(0xb88860, jumpCloth, 0xd06018, 0xd06018, 0x201810, 0x3a2410, paintFace('#b88860', '#201810')),
      camo: mk(0xa87850, camoCloth, 0x4a5a32, 0x3a4428, 0x2a2018, 0x2a2418, paintFace('#a87850', '#2a2018')),
      suit: mk(0xc8a888, suitCloth, 0x1a1a1c, 0x1a1a1c, 0x141210, 0x121212, paintFace('#c8a888', '#141210')),
      suitNavy: mk(0xc8a888, navyCloth, 0x1a2a4a, 0x4a4a52, 0x3a3028, 0x1a1a20, paintFace('#c8a888', '#3a3028')),
      agent: mk(0xc4a07a, agentCloth, 0x121214, 0x121214, 0x0e0c0a, 0x0a0a0a, paintAgentFace('#c4a07a', '#0e0c0a')),
    },
  };
}

function pushQuad(
  pos: number[],
  nrm: number[],
  uv: number[],
  idx: number[],
  a: [number, number, number],
  b: [number, number, number],
  c: [number, number, number],
  d: [number, number, number],
  nx: number,
  ny: number,
  nz: number,
  u0 = 0,
  v0 = 0,
  u1 = 1,
  v1 = 1,
): void {
  const base = pos.length / 3;
  for (const p of [a, b, c, d]) {
    pos.push(p[0], p[1], p[2]);
    nrm.push(nx, ny, nz);
  }
  uv.push(u0, v0, u1, v0, u1, v1, u0, v1);
  idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

/** Outward CCW faces. Front is −Z. `frontBack` maps chest to the left atlas panel. */
function taperBox(
  wTop: number,
  wBot: number,
  h: number,
  d: number,
  uvMode: 'shared' | 'frontBack' = 'shared',
): THREE.BufferGeometry {
  const yt = h * 0.5;
  const yb = -h * 0.5;
  const zf = -d * 0.5;
  const zr = d * 0.5;
  const wt = wTop * 0.5;
  const wb = wBot * 0.5;
  const pos: number[] = [];
  const nrm: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  const fu0 = 0;
  const fu1 = uvMode === 'frontBack' ? 0.5 : 1;
  const bu0 = uvMode === 'frontBack' ? 0.5 : 0;
  const bu1 = 1;
  pushQuad(pos, nrm, uv, idx, [-wb, yb, zf], [wb, yb, zf], [wt, yt, zf], [-wt, yt, zf], 0, 0, -1, fu0, 0, fu1, 1);
  pushQuad(pos, nrm, uv, idx, [wb, yb, zr], [-wb, yb, zr], [-wt, yt, zr], [wt, yt, zr], 0, 0, 1, bu0, 0, bu1, 1);
  pushQuad(pos, nrm, uv, idx, [-wt, yt, zf], [wt, yt, zf], [wt, yt, zr], [-wt, yt, zr], 0, 1, 0, bu0, 0, bu1, 1);
  pushQuad(pos, nrm, uv, idx, [-wb, yb, zr], [wb, yb, zr], [wb, yb, zf], [-wb, yb, zf], 0, -1, 0, bu0, 0, bu1, 1);
  pushQuad(pos, nrm, uv, idx, [-wb, yb, zr], [-wb, yb, zf], [-wt, yt, zf], [-wt, yt, zr], -1, 0, 0, bu0, 0, bu1, 1);
  pushQuad(pos, nrm, uv, idx, [wb, yb, zf], [wb, yb, zr], [wt, yt, zr], [wt, yt, zf], 1, 0, 0, bu0, 0, bu1, 1);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(idx);
  return geo;
}

function addMesh(
  parent: THREE.Object3D,
  geo: THREE.BufferGeometry,
  material: THREE.Material,
  y: number,
  sx = 1,
  sy = 1,
  sz = 1,
): THREE.Mesh {
  const mesh = new THREE.Mesh(geo, material);
  mesh.position.set(0, y, 0);
  mesh.scale.set(sx, sy, sz);
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  mesh.frustumCulled = true;
  parent.add(mesh);
  return mesh;
}

/** Shin +Y is down and +Z is forward, so the sole sits on the ground with toes ahead. */
function addBoot(foot: THREE.Bone, boots: THREE.Material): void {
  const sole = new THREE.Mesh(new THREE.SphereGeometry(0.048, 8, 6), boots);
  sole.position.set(0, 0.02, 0.052);
  sole.scale.set(1.08, 0.36, 1.85);
  sole.castShadow = false;
  sole.receiveShadow = true;
  foot.add(sole);
  const heel = new THREE.Mesh(new THREE.SphereGeometry(0.03, 7, 5), boots);
  heel.position.set(0, 0.028, -0.012);
  heel.scale.set(1.05, 0.7, 0.85);
  heel.castShadow = false;
  heel.receiveShadow = true;
  foot.add(heel);
}

function makeBone(name: string, parent: THREE.Object3D, x: number, y: number, z: number): THREE.Bone {
  const b = new THREE.Bone();
  b.name = name;
  b.position.set(x, y, z);
  parent.add(b);
  return b;
}

function bind(b: THREE.Object3D): void {
  b.userData.bindQ = b.quaternion.clone();
  b.userData.bindP = b.position.clone();
}

/** Slightly smaller than the viewmodel so hands match a rounder body. */
const FPS_HAND_SCALE = 1.65;
/** Hand bone along forearm +Y so the scaled sleeve cuff meets the elbow. */
const FPS_HAND_Y = 0.38;

function addArmLimb(
  bone: THREE.Object3D,
  mat: THREE.Material,
  y: number,
  cylLen: number,
  radius: number,
  thick: number,
): void {
  const mesh = new THREE.Mesh(new THREE.CapsuleGeometry(radius * thick, cylLen, 6, 10), mat);
  mesh.position.set(0, y, 0);
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  mesh.frustumCulled = true;
  bone.add(mesh);
}

function addShoulder(bone: THREE.Object3D, mat: THREE.Material, radius: number, thick: number): void {
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(radius * thick, 10, 8), mat);
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  mesh.frustumCulled = true;
  bone.add(mesh);
}

/** Empty Mixamo hand bone + FPS viewmodel hand (fingers along bone +Y). */
function attachFpsHand(
  forearm: THREE.Bone,
  side: 'Left' | 'Right',
  grip: ArmGripStyle,
  sleeve: THREE.Material,
): THREE.Bone {
  const hand = makeBone(`${side}Hand`, forearm, 0, FPS_HAND_Y, 0);
  // Always build the right-hand mesh, then mirror X for the left so thumbs sit inward.
  const fps = createFpsHandGroup(true, grip, sleeve);
  fps.rotation.x = Math.PI / 2;
  const s = FPS_HAND_SCALE;
  fps.scale.set(side === 'Right' ? s : -s, s, s);
  fps.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.frustumCulled = true;
  });
  hand.add(fps);
  return hand;
}

function hemiHair(radius: number, drop = 0.64): THREE.SphereGeometry {
  return new THREE.SphereGeometry(radius, 12, 8, 0, Math.PI * 2, 0, Math.PI * drop);
}

function placeHair(
  head: THREE.Bone,
  geo: THREE.BufferGeometry,
  hair: THREE.Material,
  x: number,
  y: number,
  z: number,
  sx = 1,
  sy = 1,
  sz = 1,
): THREE.Mesh {
  const mesh = new THREE.Mesh(geo, hair);
  mesh.position.set(x, y, z);
  mesh.scale.set(sx, sy, sz);
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  mesh.frustumCulled = true;
  head.add(mesh);
  return mesh;
}

/** Side-part: heavier right, scalp line, fringe stops at the brow. */
function addBondHair(head: THREE.Bone, hair: THREE.Material): void {
  placeHair(head, hemiHair(0.132, 0.48), hair, -0.018, 0.178, 0.022, 1.08, 0.78, 0.96);
  placeHair(head, new THREE.SphereGeometry(0.078, 8, 6), hair, -0.062, 0.208, 0.004, 1.12, 0.58, 1.02);
  placeHair(head, new THREE.SphereGeometry(0.036, 8, 6), hair, 0.012, 0.222, -0.042, 0.82, 0.42, 0.62);
  placeHair(head, new THREE.SphereGeometry(0.04, 8, 6), hair, -0.042, 0.216, -0.05, 1.05, 0.48, 0.64);
  placeHair(head, new THREE.SphereGeometry(0.052, 8, 6), hair, 0.108, 0.148, 0.012, 0.58, 1.22, 1.08);
  placeHair(head, new THREE.SphereGeometry(0.062, 8, 6), hair, -0.11, 0.155, 0.012, 0.68, 1.28, 1.12);
  placeHair(head, new THREE.SphereGeometry(0.082, 8, 6), hair, -0.01, 0.152, 0.082, 1.18, 0.78, 0.62);
}

function addCapHair(head: THREE.Bone, hair: THREE.Material): void {
  placeHair(head, hemiHair(0.14, 0.5), hair, 0, 0.168, 0.022, 1.04, 0.82, 0.96);
  placeHair(head, new THREE.SphereGeometry(0.052, 8, 6), hair, 0.11, 0.148, 0.016, 0.6, 1.2, 1.06);
  placeHair(head, new THREE.SphereGeometry(0.052, 8, 6), hair, -0.11, 0.148, 0.016, 0.6, 1.2, 1.06);
  placeHair(head, new THREE.SphereGeometry(0.072, 8, 6), hair, 0, 0.142, 0.088, 1.22, 0.72, 0.52);
  placeHair(head, new THREE.SphereGeometry(0.04, 8, 6), hair, 0, 0.218, -0.048, 1.22, 0.42, 0.55);
}

function addRecedeHair(head: THREE.Bone, hair: THREE.Material): void {
  placeHair(head, hemiHair(0.122, 0.46), hair, 0, 0.176, 0.048, 0.95, 0.66, 0.84);
  placeHair(head, new THREE.SphereGeometry(0.05, 8, 6), hair, 0.108, 0.142, 0.028, 0.55, 1.05, 1.0);
  placeHair(head, new THREE.SphereGeometry(0.05, 8, 6), hair, -0.108, 0.142, 0.028, 0.55, 1.05, 1.0);
  placeHair(head, new THREE.SphereGeometry(0.078, 8, 6), hair, 0, 0.148, 0.09, 1.15, 0.7, 0.55);
}

function addPuffHair(head: THREE.Bone, hair: THREE.Material): void {
  placeHair(head, hemiHair(0.148, 0.5), hair, 0, 0.192, 0.02, 1.1, 1.08, 0.98);
  placeHair(head, new THREE.SphereGeometry(0.06, 8, 6), hair, 0.118, 0.168, 0.016, 0.7, 1.15, 1.05);
  placeHair(head, new THREE.SphereGeometry(0.06, 8, 6), hair, -0.118, 0.168, 0.016, 0.7, 1.15, 1.05);
  placeHair(head, new THREE.SphereGeometry(0.042, 8, 6), hair, 0, 0.226, -0.046, 1.18, 0.5, 0.58);
}

const TIE_COLOR: Record<Exclude<TieStyle, 'none'>, { blade: number; knot: number }> = {
  navy: { blade: 0x1c2a4a, knot: 0x121c30 },
  red: { blade: 0xc4181c, knot: 0x8e1216 },
  gold: { blade: 0xc4a020, knot: 0x8a7010 },
};

function addCollarAndTie(spine1: THREE.Bone, spine2: THREE.Bone, neck: THREE.Bone, tie: TieStyle): void {
  const shirt = new THREE.MeshStandardMaterial({
    color: 0xf4f0e8,
    roughness: 0.84,
    metalness: 0.02,
    flatShading: true,
  });

  const shirtFront = new THREE.Mesh(taperBox(0.1, 0.088, 0.2, 0.028), shirt);
  shirtFront.position.set(0, 0.07, -0.1);
  spine1.add(shirtFront);

  if (tie !== 'none') {
    const col = TIE_COLOR[tie];
    const bladeMat = new THREE.MeshStandardMaterial({
      color: col.blade,
      roughness: 0.48,
      metalness: 0.06,
      flatShading: true,
    });
    const knotMat = new THREE.MeshStandardMaterial({
      color: col.knot,
      roughness: 0.45,
      metalness: 0.05,
      flatShading: true,
    });
    const blade = new THREE.Mesh(taperBox(0.022, 0.044, 0.2, 0.016), bladeMat);
    blade.position.set(0, 0.012, -0.118);
    spine1.add(blade);
    const knotMesh = new THREE.Mesh(taperBox(0.034, 0.028, 0.034, 0.024), knotMat);
    knotMesh.position.set(0, 0.1, -0.108);
    spine2.add(knotMesh);
  }

  const collarL = new THREE.Mesh(taperBox(0.055, 0.038, 0.032, 0.068), shirt);
  collarL.position.set(0.04, 0.012, -0.072);
  collarL.rotation.set(0.52, 0.52, 0.08);
  neck.add(collarL);
  const collarR = new THREE.Mesh(taperBox(0.055, 0.038, 0.032, 0.068), shirt);
  collarR.position.set(-0.04, 0.012, -0.072);
  collarR.rotation.set(0.52, -0.52, -0.08);
  neck.add(collarR);
}

function addHair(head: THREE.Bone, style: HairStyle, hair: THREE.Material): void {
  if (style === 'bald') return;
  if (style === 'part') {
    addBondHair(head, hair);
    return;
  }
  if (style === 'recede') {
    addRecedeHair(head, hair);
    return;
  }
  if (style === 'puff') {
    addPuffHair(head, hair);
    return;
  }
  addCapHair(head, hair);
}

function addGlasses(head: THREE.Bone): void {
  const frame = new THREE.MeshStandardMaterial({
    color: 0x1a1a1c,
    roughness: 0.35,
    metalness: 0.45,
    flatShading: true,
  });
  const lens = new THREE.MeshStandardMaterial({
    color: 0x88aacc,
    roughness: 0.12,
    metalness: 0.2,
    transparent: true,
    opacity: 0.22,
    flatShading: true,
  });
  const g = new THREE.Group();
  g.name = 'glasses';
  const left = new THREE.Mesh(new THREE.BoxGeometry(0.066, 0.036, 0.012), lens);
  left.position.set(0.044, 0.128, -0.126);
  const right = new THREE.Mesh(new THREE.BoxGeometry(0.066, 0.036, 0.012), lens);
  right.position.set(-0.044, 0.128, -0.126);
  const rimL = new THREE.Mesh(new THREE.BoxGeometry(0.072, 0.042, 0.008), frame);
  rimL.position.set(0.044, 0.128, -0.12);
  const rimR = new THREE.Mesh(new THREE.BoxGeometry(0.072, 0.042, 0.008), frame);
  rimR.position.set(-0.044, 0.128, -0.12);
  const bridge = new THREE.Mesh(new THREE.BoxGeometry(0.026, 0.01, 0.008), frame);
  bridge.position.set(0, 0.132, -0.122);
  g.add(left, right, rimL, rimR, bridge);
  head.add(g);
}

function tintHands(hand: THREE.Object3D, skin: THREE.Material): void {
  const shared = playerArmMaterials().skin;
  hand.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh && mesh.material === shared) mesh.material = skin;
  });
}

/**
 * Front-only skull cap (−Z). A full textured sphere interpolates eye UVs
 * across side triangles and paints them on the back of the head.
 */
function frontFaceGeo(): THREE.SphereGeometry {
  const r = 0.13;
  const span = Math.PI * 0.72;
  const geo = new THREE.SphereGeometry(r, 12, 10, Math.PI * 1.5 - span / 2, span);
  const pos = geo.attributes.position;
  const uv = geo.attributes.uv;
  const ku = 0.65;
  const kv = 0.55;
  for (let i = 0; i < pos.count; i++) {
    uv.setXY(
      i,
      THREE.MathUtils.clamp(0.5 + (pos.getX(i) / r) * ku, 0, 1),
      THREE.MathUtils.clamp(0.5 + (pos.getY(i) / r) * kv, 0, 1),
    );
  }
  uv.needsUpdate = true;
  return geo;
}

function tagHeadgear(root: THREE.Object3D, kind: 'hat' | 'helmet'): void {
  root.traverse((o) => {
    o.userData.headgear = kind;
  });
}

function addGuardHat(head: THREE.Bone): THREE.Group {
  const felt = new THREE.MeshStandardMaterial({
    color: 0x2a3c22,
    roughness: 0.72,
    metalness: 0.08,
    flatShading: true,
  });
  const brim = new THREE.MeshStandardMaterial({
    color: 0x141610,
    roughness: 0.55,
    metalness: 0.12,
    flatShading: true,
  });
  const band = new THREE.MeshStandardMaterial({
    color: 0x1a1810,
    roughness: 0.6,
    metalness: 0.1,
    flatShading: true,
  });
  const g = new THREE.Group();
  g.name = 'guard_hat';
  const crown = new THREE.Mesh(hemiHair(0.138), felt);
  crown.position.set(0, 0.2, 0.008);
  g.add(crown);
  const peak = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.016, 0.09), brim);
  peak.position.set(0, 0.2, -0.13);
  g.add(peak);
  const strap = new THREE.Mesh(new THREE.CylinderGeometry(0.132, 0.134, 0.02, 12), band);
  strap.position.set(0, 0.205, 0.008);
  g.add(strap);
  tagHeadgear(g, 'hat');
  head.add(g);
  return g;
}

function addGuardHelmet(head: THREE.Bone): THREE.Group {
  const blue = new THREE.MeshStandardMaterial({
    color: 0x2a5aaa,
    roughness: 0.38,
    metalness: 0.35,
    flatShading: true,
  });
  const rim = new THREE.MeshStandardMaterial({
    color: 0x1a2a48,
    roughness: 0.45,
    metalness: 0.4,
    flatShading: true,
  });
  const g = new THREE.Group();
  g.name = 'guard_helmet';
  const dome = new THREE.Mesh(new THREE.SphereGeometry(0.142, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.58), blue);
  dome.position.set(0, 0.2, 0.008);
  g.add(dome);
  const skirt = new THREE.Mesh(new THREE.CylinderGeometry(0.138, 0.145, 0.036, 10), rim);
  skirt.position.set(0, 0.162, 0.008);
  g.add(skirt);
  const visor = new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.028, 0.055), rim);
  visor.position.set(0, 0.188, -0.118);
  g.add(visor);
  tagHeadgear(g, 'helmet');
  head.add(g);
  return g;
}

function addHead(
  neck: THREE.Bone,
  skin: THREE.Material,
  hair: THREE.Material,
  faceMat: THREE.Material,
  headgear: HeadgearId,
  look: CharLook,
): THREE.Bone {
  const head = makeBone('Head', neck, 0, 0.05, 0);
  addMesh(head, new THREE.SphereGeometry(0.13, 12, 10), skin, 0.13, 1.02, 1.08, 0.94);
  const face = addMesh(head, frontFaceGeo(), faceMat, 0.13, 1.035, 1.095, 0.955);
  face.name = 'face';
  face.castShadow = false;
  if (headgear !== 'helmet') addHair(head, look.hairStyle, hair);
  if (look.glasses) addGlasses(head);
  return head;
}

/** Front-atlas card (u 0–0.5) so ties / badges stay on the chest. */
function addChestCard(parent: THREE.Object3D, mat: THREE.Material, thick: number): void {
  const geo = new THREE.PlaneGeometry(0.2 * thick, 0.24);
  const uv = geo.attributes.uv;
  for (let i = 0; i < uv.count; i++) uv.setX(i, uv.getX(i) * 0.5);
  const nrm = geo.attributes.normal;
  for (let i = 0; i < nrm.count; i++) nrm.setXYZ(i, 0, 0, -1);
  const idx = geo.index;
  if (idx) {
    for (let i = 0; i < idx.count; i += 3) {
      const tmp = idx.getX(i);
      idx.setX(i, idx.getX(i + 2));
      idx.setX(i + 2, tmp);
    }
  }
  const card = new THREE.Mesh(geo, mat);
  card.position.set(0, 0.07, -0.108 * thick);
  card.renderOrder = 1;
  card.castShadow = false;
  card.receiveShadow = true;
  card.frustumCulled = true;
  parent.add(card);
}

export function buildProcEnemy(
  variant: EnemyVariantId,
  kit: ProcEnemyKit,
  brute = false,
  kind: ProcEnemyKind = 'pistol',
  headgear: HeadgearId = 'none',
  look: CharLook = DEFAULT_LOOK,
): THREE.Group {
  const m = kit.variants[variant];
  const thick = brute ? 1.18 : 1;
  const short = brute ? 0.94 : 1;
  const grip = enemyGripStyle(kind);
  const coat =
    variant === 'scientist' ||
    variant === 'suit' ||
    variant === 'suitNavy' ||
    variant === 'agent' ||
    variant === 'guard';
  const sleeve = m.sleeve;
  const skin = m.skin.clone();
  skin.color.setStyle(look.skin);
  const hair = m.hair.clone();
  hair.color.setStyle(look.hair);

  const root = new THREE.Group();
  root.name = 'procEnemy';

  const hips = makeBone('Hips', root, 0, 0.86 * short, 0);
  const spine = makeBone('Spine', hips, 0, 0.05, 0);
  const spine1 = makeBone('Spine1', spine, 0, 0.1, 0);
  const spine2 = makeBone('Spine2', spine1, 0, 0.1, 0);
  const neck = makeBone('Neck', spine2, 0, 0.08, 0);
  const head = addHead(neck, skin, hair, m.face, headgear, look);
  if (headgear === 'hat') addGuardHat(head);
  if (headgear === 'helmet') addGuardHelmet(head);
  if (coat && variant !== 'guard') addCollarAndTie(spine1, spine2, neck, look.tie);

  addArmLimb(hips, m.pants, 0.02, 0.05, 0.1, thick);
  addArmLimb(spine1, m.clothes, 0.08, 0.14, 0.105, thick);
  addChestCard(spine1, m.clothes, thick);
  addArmLimb(neck, skin, 0.02, 0.04, 0.038, 1);

  // Character faces −Z, so anatomical left is −X and right is +X.
  const leftArm = makeBone('LeftArm', spine2, -0.18 * thick, 0.04, 0);
  // Identity +Y is up; hang at the side (A-pose). Old z=±1.15 folded both arms inward.
  leftArm.rotation.x = Math.PI;
  leftArm.rotation.z = 0.22;
  const leftFore = makeBone('LeftForeArm', leftArm, 0, 0.26, 0);
  leftFore.rotation.z = -0.08;
  addShoulder(leftArm, sleeve, 0.05, thick);
  addArmLimb(leftArm, sleeve, 0.13, 0.24, 0.06, thick);
  const leftHand = attachFpsHand(leftFore, 'Left', grip, sleeve);
  tintHands(leftHand, skin);

  const rightArm = makeBone('RightArm', spine2, 0.18 * thick, 0.04, 0);
  rightArm.rotation.x = Math.PI;
  rightArm.rotation.z = -0.22;
  const rightFore = makeBone('RightForeArm', rightArm, 0, 0.26, 0);
  rightFore.rotation.z = 0.08;
  addShoulder(rightArm, sleeve, 0.05, thick);
  addArmLimb(rightArm, sleeve, 0.13, 0.24, 0.06, thick);
  const rightHand = attachFpsHand(rightFore, 'Right', grip, sleeve);
  tintHands(rightHand, skin);

  const leftUp = makeBone('LeftUpLeg', hips, -0.09, -0.02, 0);
  leftUp.rotation.x = Math.PI;
  leftUp.rotation.z = 0.06;
  const leftLeg = makeBone('LeftLeg', leftUp, 0, 0.36 * short, 0);
  const leftFoot = makeBone('LeftFoot', leftLeg, 0, 0.34 * short, 0);
  leftFoot.rotation.x = 0.08;
  addArmLimb(leftUp, m.pants, 0.17 * short, 0.28 * short, 0.068, thick);
  addArmLimb(leftLeg, m.pants, 0.16 * short, 0.26 * short, 0.055, thick);
  addBoot(leftFoot, m.boots);

  const rightUp = makeBone('RightUpLeg', hips, 0.09, -0.02, 0);
  rightUp.rotation.x = Math.PI;
  rightUp.rotation.z = -0.06;
  const rightLeg = makeBone('RightLeg', rightUp, 0, 0.36 * short, 0);
  const rightFoot = makeBone('RightFoot', rightLeg, 0, 0.34 * short, 0);
  rightFoot.rotation.x = 0.08;
  addArmLimb(rightUp, m.pants, 0.17 * short, 0.28 * short, 0.068, thick);
  addArmLimb(rightLeg, m.pants, 0.16 * short, 0.26 * short, 0.055, thick);
  addBoot(rightFoot, m.boots);

  const bones = {
    Hips: hips,
    Spine: spine,
    Spine1: spine1,
    Spine2: spine2,
    Neck: neck,
    Head: head,
    LeftArm: leftArm,
    LeftForeArm: leftFore,
    LeftHand: leftHand,
    RightArm: rightArm,
    RightForeArm: rightFore,
    RightHand: rightHand,
    LeftUpLeg: leftUp,
    LeftLeg: leftLeg,
    LeftFoot: leftFoot,
    RightUpLeg: rightUp,
    RightLeg: rightLeg,
    RightFoot: rightFoot,
  };
  for (const b of Object.values(bones)) bind(b);
  root.userData.procBones = bones;

  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.castShadow = false;
      mesh.receiveShadow = true;
      mesh.frustumCulled = true;
    }
  });
  return root;
}

function resetBind(b: THREE.Object3D | undefined): void {
  if (!b) return;
  const q = b.userData.bindQ as THREE.Quaternion | undefined;
  const p = b.userData.bindP as THREE.Vector3 | undefined;
  if (q) b.quaternion.copy(q);
  if (p) b.position.copy(p);
}

function resetAll(bones: Record<string, THREE.Object3D>): void {
  for (const b of Object.values(bones)) resetBind(b);
}

function curlFingers(hand: THREE.Object3D | undefined, curl: number, thumb: number): void {
  if (!hand) return;
  hand.traverse((o) => {
    const n = o.name;
    if (/Hand(Index|Middle|Ring|Pinky)1$/.test(n)) o.rotation.x = -curl;
    else if (/Hand(Index|Middle|Ring|Pinky)2$/.test(n)) o.rotation.x = -curl * 0.85;
    else if (/Hand(Index|Middle|Ring|Pinky)3$/.test(n)) o.rotation.x = -curl * 0.6;
    else if (/HandThumb1$/.test(n)) o.rotation.x = -0.35 - thumb * 0.4;
    else if (/HandThumb2$/.test(n)) o.rotation.x = -thumb * 0.5;
  });
}

function poseLegs(
  bones: Record<string, THREE.Object3D>,
  phase: number,
  amp: number,
  lift: number,
): void {
  const s = Math.sin(phase);
  const c = Math.cos(phase);
  bones.LeftUpLeg?.rotateX(s * amp);
  bones.RightUpLeg?.rotateX(-s * amp);
  bones.LeftLeg?.rotateX(Math.max(0, -s) * lift);
  bones.RightLeg?.rotateX(Math.max(0, s) * lift);
  bones.Hips?.rotateY(s * 0.04);
  bones.Hips?.rotateX(Math.abs(c) * 0.03);
}

function poseArmSwing(bones: Record<string, THREE.Object3D>, phase: number, amp: number): void {
  const s = Math.sin(phase);
  bones.LeftArm?.rotateX(-s * amp);
  bones.RightArm?.rotateX(s * amp);
}

const _boneUp = new THREE.Vector3(0, 1, 0);
const _boneAim = new THREE.Vector3();

/** Point a +Y bone (arm chain) in model space. Face / muzzle forward is −Z. */
function pointBoneY(bone: THREE.Object3D | undefined, x: number, y: number, z: number, roll = 0): void {
  if (!bone) return;
  _boneAim.set(x, y, z).normalize();
  bone.quaternion.setFromUnitVectors(_boneUp, _boneAim);
  if (roll) bone.rotateY(roll);
}

const HOLD_BLEND_BONES = [
  'Spine',
  'Spine1',
  'Spine2',
  'RightArm',
  'RightForeArm',
  'RightHand',
  'LeftArm',
  'LeftForeArm',
  'LeftHand',
] as const;

const _holdHip: THREE.Quaternion[] = HOLD_BLEND_BONES.map(() => new THREE.Quaternion());
const _holdAim: THREE.Quaternion[] = HOLD_BLEND_BONES.map(() => new THREE.Quaternion());
const _holdReady: THREE.Quaternion[] = HOLD_BLEND_BONES.map(() => new THREE.Quaternion());
const _holdSprint: THREE.Quaternion[] = HOLD_BLEND_BONES.map(() => new THREE.Quaternion());

function snapshotHold(bones: Record<string, THREE.Object3D>, out: THREE.Quaternion[]): void {
  for (let i = 0; i < HOLD_BLEND_BONES.length; i++) {
    const b = bones[HOLD_BLEND_BONES[i]!];
    if (b) out[i]!.copy(b.quaternion);
  }
}

function applyHoldBlend(
  bones: Record<string, THREE.Object3D>,
  from: THREE.Quaternion[],
  to: THREE.Quaternion[],
  t: number,
): void {
  const u = THREE.MathUtils.clamp(t, 0, 1);
  for (let i = 0; i < HOLD_BLEND_BONES.length; i++) {
    const b = bones[HOLD_BLEND_BONES[i]!];
    if (b) b.quaternion.copy(from[i]!).slerp(to[i]!, u);
  }
}

function poseHip(bones: Record<string, THREE.Object3D>, kind: ProcEnemyKind): void {
  const longGun = kind === 'mp5' || kind === 'ak47' || kind === 'shotgun' || kind === 'sawedoff';
  const shotgun = kind === 'shotgun' || kind === 'sawedoff';

  bones.Spine?.rotateX(0.05);
  bones.Spine1?.rotateX(0.03);

  pointBoneY(bones.RightArm, 0.26, -0.24, -0.9, -0.1);
  bones.RightForeArm.rotation.set(shotgun ? 0.55 : 0.44, 0, 0.12);
  bones.RightHand.rotation.set(-0.06, -0.1, 0.08);

  if (longGun) {
    pointBoneY(bones.LeftArm, 0.04, -0.2, -0.88, 0.18);
    bones.LeftForeArm.rotation.set(shotgun ? 0.7 : 0.58, 0, -0.14);
    bones.LeftHand.rotation.set(0.06, 0.1, 0);
  } else {
    pointBoneY(bones.LeftArm, 0.3, -0.14, -0.82, 0.22);
    bones.LeftForeArm.rotation.set(0.72, 0, -0.16);
    bones.LeftHand.rotation.set(0.04, 0.08, 0);
  }

  curlFingers(bones.RightHand, 0.85, 0.7);
  curlFingers(bones.LeftHand, longGun ? 0.65 : 0.72, 0.55);
}

function poseSprintGun(bones: Record<string, THREE.Object3D>): void {
  bones.Spine?.rotateX(0.08);
  pointBoneY(bones.RightArm, 0.4, -0.62, -0.5, -0.08);
  bones.RightForeArm.rotation.set(0.32, 0, 0.1);
  bones.RightHand.rotation.set(0.04, -0.08, 0.16);
  pointBoneY(bones.LeftArm, -0.22, -0.92, -0.14, 0.06);
  bones.LeftForeArm.rotation.set(0.14, 0, -0.06);
  curlFingers(bones.RightHand, 0.85, 0.7);
  curlFingers(bones.LeftHand, 0.25, 0.2);
}

function resetHoldBones(bones: Record<string, THREE.Object3D>): void {
  for (const name of HOLD_BLEND_BONES) resetBind(bones[name]);
}

/** Hip ready (ads=0) through eye-level aim (ads=1), then sprint tuck. */
export function poseHold(
  bones: Record<string, THREE.Object3D>,
  kind: ProcEnemyKind,
  ads: number,
  sprint = 0,
): void {
  const a = THREE.MathUtils.clamp(ads, 0, 1);
  const s = THREE.MathUtils.clamp(sprint, 0, 1);
  resetHoldBones(bones);
  poseHip(bones, kind);
  snapshotHold(bones, _holdHip);
  resetHoldBones(bones);
  poseAim(bones, kind, 0);
  snapshotHold(bones, _holdAim);
  applyHoldBlend(bones, _holdHip, _holdAim, a);
  if (s < 0.02) return;
  snapshotHold(bones, _holdReady);
  resetHoldBones(bones);
  poseSprintGun(bones);
  snapshotHold(bones, _holdSprint);
  applyHoldBlend(bones, _holdReady, _holdSprint, s);
}

function poseAim(bones: Record<string, THREE.Object3D>, kind: ProcEnemyKind, kick: number): void {
  const longGun = kind === 'mp5' || kind === 'ak47' || kind === 'shotgun' || kind === 'sawedoff';
  const shotgun = kind === 'shotgun' || kind === 'sawedoff';

  bones.Spine?.rotateX(-0.06 - kick * 0.1);
  bones.Spine1?.rotateX(-0.05);
  bones.Spine2?.rotateY(0.05);

  // Gun hand: present the weapon toward the face (−Z), elbow slightly dropped.
  pointBoneY(bones.RightArm, 0.1, 0.14 - kick * 0.18, -1, -0.18);
  bones.RightForeArm.rotation.set(shotgun ? 0.32 : 0.2, 0, 0.14);
  bones.RightHand.rotation.set(-0.1 - kick * 0.22, -0.12, 0);

  if (longGun) {
    // Support hand on the forend — forward, not hugged across the ribs.
    pointBoneY(bones.LeftArm, -0.08, 0.04, -1, 0.22);
    bones.LeftForeArm.rotation.set(shotgun ? 0.52 : 0.42, 0, -0.16);
    bones.LeftHand.rotation.set(0.08, 0.12, 0);
  } else {
    // Pistol: left hand comes to the grip, still in front of the chest.
    pointBoneY(bones.LeftArm, 0.2, 0.06, -1, 0.28);
    bones.LeftForeArm.rotation.set(0.58, 0, -0.2);
    bones.LeftHand.rotation.set(0.04, 0.08, 0);
  }

  curlFingers(bones.RightHand, 0.85, 0.7);
  curlFingers(bones.LeftHand, longGun ? 0.65 : 0.72, 0.55);
}

export function poseGuard(bones: Record<string, THREE.Object3D>): void {
  pointBoneY(bones.LeftArm, -0.15, 0.6, -0.22, 0.08);
  pointBoneY(bones.RightArm, 0.15, 0.6, -0.22, -0.08);
  bones.LeftForeArm.rotation.set(1.28, 0, -0.1);
  bones.RightForeArm.rotation.set(1.24, 0, 0.1);
  curlFingers(bones.LeftHand, 1.15, 0.8);
  curlFingers(bones.RightHand, 1.15, 0.8);
}

/** Map an FPS fist xform onto a third-person +Y arm chain. Face / punch is −Z. */
function posePunchArm(
  bones: Record<string, THREE.Object3D>,
  side: 'Left' | 'Right',
  fist: FistXform,
): void {
  const sign = side === 'Right' ? 1 : -1;
  const sx = sign * 0.2;
  const sy = 1.32;
  const sz = 0.02;
  const tx = fist.x * 1.85;
  const ty = 1.15 + (fist.y + 0.3) * 1.2;
  const tz = fist.z * 1.75;
  pointBoneY(bones[`${side}Arm`], tx - sx, ty - sy, tz - sz, -sign * 0.12);
  const reach = THREE.MathUtils.clamp((-fist.z - 0.22) / 0.55, 0, 1);
  const fore = bones[`${side}ForeArm`];
  if (fore) {
    fore.rotation.set(
      THREE.MathUtils.lerp(1.22, 0.08, reach),
      0,
      sign * THREE.MathUtils.lerp(0.14, 0.04, reach),
    );
  }
  const hand = bones[`${side}Hand`];
  if (hand) hand.rotation.set(fist.rx * 0.45, fist.ry * 0.4, fist.rz * 0.35);
}

export function poseEnemyPunch(bones: Record<string, THREE.Object3D>, clip: PunchClip, u: number): void {
  const fists = applyStance(samplePunchClip(clip, u), 1);
  posePunchArm(bones, 'Right', fists.right);
  posePunchArm(bones, 'Left', fists.left);
  const ext = Math.max(0, -Math.min(fists.right.z, fists.left.z) - 0.35);
  bones.Spine2?.rotateY((fists.right.z - fists.left.z) * 0.45);
  bones.Spine1?.rotateX(-ext * 0.28);
  bones.Spine?.rotateX(-ext * 0.14);
}


/**
 * Kneeling fire from the "close" pose: keep the rear knee, stand the front thigh up.
 */
function poseKneelShoot(bones: Record<string, THREE.Object3D>, w = 1): void {
  if (w < 0.02) return;
  if (bones.Hips) bones.Hips.position.y -= 0.35 * w;
  bones.Hips?.rotateY(-0.1 * w);
  bones.Spine?.rotateX(-0.12 * w);
  bones.Spine1?.rotateX(-0.06 * w);

  bones.LeftUpLeg?.rotateX(1.5 * w);
  bones.LeftUpLeg?.rotateZ(0.05 * w);
  bones.LeftLeg?.rotateX(-1.52 * w);
  bones.LeftFoot?.rotateX(-0.16 * w);

  bones.RightUpLeg?.rotateX(-0.06 * w);
  bones.RightUpLeg?.rotateZ(-0.04 * w);
  bones.RightLeg?.rotateX(-1.48 * w);
  bones.RightFoot?.rotateX(-1.45 * w);
}

/** Toilet / chair sit: same as kneel, but both legs use the raised (left) kneel leg. */
function poseSit(bones: Record<string, THREE.Object3D>, w: number): void {
  if (w < 0.02) return;
  if (bones.Hips) bones.Hips.position.y -= 0.35 * w;
  bones.Spine?.rotateX(-0.12 * w);
  bones.Spine1?.rotateX(-0.06 * w);

  bones.LeftUpLeg?.rotateX(1.5 * w);
  bones.LeftUpLeg?.rotateZ(0.05 * w);
  bones.LeftLeg?.rotateX(-1.52 * w);
  bones.LeftFoot?.rotateX(-0.16 * w);

  bones.RightUpLeg?.rotateX(1.5 * w);
  bones.RightUpLeg?.rotateZ(-0.05 * w);
  bones.RightLeg?.rotateX(-1.52 * w);
  bones.RightFoot?.rotateX(-0.16 * w);
}

function posePoint(bones: Record<string, THREE.Object3D>, melee: boolean): void {
  bones.Spine?.rotateX(-0.06);
  pointBoneY(bones.RightArm, -0.04, 0.18, -1, -0.08);
  bones.RightForeArm.rotation.set(0.08, 0, 0);
  bones.RightHand.rotation.set(-0.1, -0.08, 0);
  pointBoneY(bones.LeftArm, -0.22, -0.75, -0.2, 0);
  bones.LeftForeArm.rotation.set(0.2, 0, 0.06);
  if (melee) {
    curlFingers(bones.RightHand, 0.95, 0.55);
    bones.RightHand.traverse((o) => {
      if (/RightHandIndex/.test(o.name)) o.rotation.x = 0.05;
    });
  } else {
    curlFingers(bones.RightHand, 0.7, 0.5);
    curlFingers(bones.LeftHand, 0.35, 0.25);
  }
}

export function applyProcEnemyPose(e: ProcAnimEnemy, dt: number): void {
  const bones = e.model.userData.procBones as Record<string, THREE.Object3D> | undefined;
  if (!bones) return;

  const lower = e.layerMode === 'aimloco' ? e.lowerClip : e.currentClip;
  const upper = e.layerMode === 'aimloco' ? e.upperClip : e.currentClip;
  const moving = lower === 'walk' || lower === 'run' || lower === 'sidestep' || lower === 'hop';
  const run = lower === 'run' || lower === 'hop';
  const rate = run ? 10 : moving ? 7.2 : 1.6;
  e.animT += dt * rate;

  resetAll(bones);

  const kneelTo = e.kneelTarget ?? (lower === 'kneel' ? 1 : 0);
  e.kneelBlend = e.kneelBlend ?? 0;
  e.kneelBlend += (kneelTo - e.kneelBlend) * (1 - Math.exp(-7.2 * dt));
  const kneel = e.kneelBlend;

  if (kneel > 0.02) {
    poseKneelShoot(bones, kneel);
  } else if (moving) {
    const amp = run ? 0.72 : 0.48;
    poseLegs(bones, e.animT, amp, run ? 0.95 : 0.65);
    if (lower === 'sidestep') {
      bones.Hips?.rotateZ(Math.sin(e.animT) * 0.12);
    }
    if (lower === 'hop') {
      bones.Hips?.rotateX(-0.2);
      if (bones.Hips) bones.Hips.position.y += Math.abs(Math.sin(e.animT * 0.5)) * 0.08;
    }
  } else {
    bones.Hips?.rotateX(Math.sin(e.animT * 0.35) * 0.015);
  }

  const melee = e.kind === 'melee';
  const punch = bakedPunchClip(e.punchId ?? 'jabR');
  if (e.attackPhase === 'swing' || upper === 'melee') {
    const dur = punchPlayDuration(punch);
    poseEnemyPunch(bones, punch, 1 - Math.min(1, e.attackLockT / dur));
  } else if (e.attackPhase === 'windup') {
    const chamber = punch.keys.find((k) => k.id === 'pos1')?.t ?? 0.2;
    poseEnemyPunch(bones, punch, chamber);
  } else if (upper === 'guard' || (melee && (upper === 'idle' || upper === 'aim'))) {
    poseGuard(bones);
    if (moving && !melee) poseArmSwing(bones, e.animT, 0.25);
  } else if (
    upper === 'aim' ||
    upper === 'shoot' ||
    e.currentClip.startsWith('aim') ||
    e.attackPhase === 'shoot' ||
    e.attackPhase === 'aim'
  ) {
    const kick =
      e.attackPhase === 'shoot' || upper === 'shoot' ? Math.min(1, e.attackLockT * 8) : 0;
    poseAim(bones, e.kind, kick);
  } else if (upper === 'point' || e.currentClip === 'point') {
    posePoint(bones, e.kind === 'melee');
  } else if (upper === 'lookaround') {
    if (moving) poseArmSwing(bones, e.animT, 0.4);
    else curlFingers(bones.LeftHand, 0.25, 0.2);
  } else if (upper === 'hurt_foot' || e.currentClip === 'hurt_foot') {
    bones.RightUpLeg?.rotateX(-0.7);
    bones.Spine1?.rotateX(0.15);
  } else if (upper === 'hurt_arm' || e.currentClip === 'hurt_arm') {
    pointBoneY(bones.LeftArm, -0.35, -0.15, -0.55, -0.2);
    bones.LeftForeArm.rotation.set(0.85, 0, -0.15);
  } else if (upper === 'hit' || e.currentClip === 'hit') {
    const hurtT = e.hurtT ?? 0;
    const w = hurtT > 0 ? Math.min(1, 0.35 + hurtT * 1.1) : 0.7;
    bones.Spine1?.rotateX(0.42 * w);
    bones.Spine2?.rotateY(-0.32 * w);
    bones.Spine2?.rotateX(0.18 * w);
    bones.Head?.rotateX(0.28 * w);
    bones.LeftArm?.rotateX(-0.55 * w);
    bones.RightArm?.rotateX(-0.4 * w);
  } else {
    if (moving) poseArmSwing(bones, e.animT, run ? 0.7 : 0.45);
    else {
      bones.LeftArm?.rotateX(Math.sin(e.animT * 0.4) * 0.04);
      bones.RightArm?.rotateX(-Math.sin(e.animT * 0.4) * 0.04);
      curlFingers(bones.LeftHand, 0.28, 0.2);
      curlFingers(bones.RightHand, 0.28, 0.2);
    }
  }

  const lockHead =
    !!e.lockHead || e.attackPhase === 'aim' || e.attackPhase === 'shoot' || upper === 'aim' || upper === 'shoot';
  const wantScan = !lockHead && upper === 'lookaround';
  const scanTo = wantScan ? 1 : 0;
  e.lookScan = e.lookScan ?? scanTo;
  e.lookScan += (scanTo - e.lookScan) * (1 - Math.exp(-6.5 * dt));
  if (e.lookScan > 0.008) {
    bones.Neck?.rotateY(Math.sin(e.animT * 0.45) * 0.55 * e.lookScan);
    bones.Head?.rotateY(Math.sin(e.animT * 0.45 + 0.2) * 0.25 * e.lookScan);
  }

  const sit = e.sitBlend ?? 0;
  if (sit > 0.02) poseSit(bones, sit);
}
